import { Anchor } from "@mantine/core";
import { IconRefresh } from "@tabler/icons-react";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  useMantineReactTable
} from "mantine-react-table-open";
import { useCallback, useMemo, useState } from "react";
import { useMachineAccounts } from "../../../../hooks/useMachineAccounts";
import { getDefaultMRTOptions } from "../../../../mantine/getDefaultMRTOptions";
import { getDefaultMRTRowEditModeOptions } from "../../../../mantine/getDefaultMRTRowEditModeOptions";
import { getMRTTopToolbarCustomActionsRenderer } from "../../../../mantine/getMRTTopToolbarCustomActionsRenderer";
import { useToast } from "../../../../mantine/notifications/useToast";
import type { MachineAccountConfiguration } from "../../../../utils/backend-types";
import { MachineAccountReimportStatus } from "../../../../utils/backend-types";
import { MultiConfirmationModal } from "../../../BuildingBlocks/Layout/Modals/MultiConfirmationModal/MultiConfirmationModal";
import { LoadOrError } from "../../../LoadOrError/LoadOrError";
import { useShouldShowStaffView } from "../../../StaffViewToggle/useShouldShowStaffView";
import { EditMachineAccountModal } from "./EditMachineAccountModal";
import "./MachineAccountPage.scss";
import { ReimportMachineAccountModal } from "./ReimportMachineAccountModal";

enum TableColumn {
  Email = "email",
  ParameterSet = "parameterSetName"
}

enum ModalType {
  New = "New",
  Edit = "Edit",
  Delete = "Delete",
  Reimport = "Reimport"
}

interface ActiveModal {
  type: ModalType;
  item: MachineAccountConfiguration | null;
}

export interface MachineAccountPageProps {
  variantId: number;
}

function MachineAccountPage({ variantId }: MachineAccountPageProps) {
  const { showErrorToast } = useToast();
  const {
    machineAccountConfiguration,
    isLoading,
    error,
    deleteMachineAccountConfiguration,
    reimportForMachineAccountConfiguration
  } = useMachineAccounts(variantId);

  const { shouldShowStaffView } = useShouldShowStaffView();

  const [activeModal, setActiveModal] = useState<ActiveModal | null>(null);
  const [reimportingIds, setReimportingIds] = useState<number[]>([]);

  const handleEmailClick = (cellData: MachineAccountConfiguration) => {
    setActiveModal({
      type: ModalType.Edit,
      item: cellData
    });
  };

  const onAfterReimport = useCallback(
    (id: number) => {
      setReimportingIds(reimportingIds.filter((currentId) => currentId !== id));
    },
    [reimportingIds]
  );

  const onReimport = useCallback(
    async (id: number) => {
      setReimportingIds([...reimportingIds, id]);
      await reimportForMachineAccountConfiguration(id, variantId);
      onAfterReimport(id);
    },
    [
      onAfterReimport,
      reimportForMachineAccountConfiguration,
      reimportingIds,
      variantId
    ]
  );

  const closeModal = useCallback(() => setActiveModal(null), []);

  const handleClickDelete = useCallback(async () => {
    try {
      await deleteMachineAccountConfiguration(activeModal?.item?.id);
    } catch (e) {
      showErrorToast(e);
    }
  }, [
    activeModal?.item?.id,
    deleteMachineAccountConfiguration,
    showErrorToast
  ]);

  const renderModal = useCallback(() => {
    if (activeModal === null) {
      return;
    }

    switch (activeModal.type) {
      case ModalType.Edit:
        return (
          <EditMachineAccountModal
            machineAccount={activeModal.item}
            variantId={variantId}
            onClose={closeModal}
          />
        );
      case ModalType.New:
        return (
          <EditMachineAccountModal
            machineAccount={null}
            variantId={variantId}
            onClose={closeModal}
          />
        );
      case ModalType.Delete:
        return (
          <MultiConfirmationModal
            actionName="löschen"
            actionObjects={[activeModal.item?.email || ""]}
            confirmationText="Möchten Sie die folgende Adresse wirklich löschen?"
            isModalOpen
            objectName="Machine Account"
            toggleModal={closeModal}
            onAction={handleClickDelete}
          />
        );
      case ModalType.Reimport:
        return (
          <ReimportMachineAccountModal
            machineAccount={activeModal.item}
            onClose={closeModal}
            onSubmit={onReimport}
          />
        );
      default:
        console.error(`Modal type ${activeModal.type} not implemented`);
        return null;
    }
  }, [activeModal, closeModal, handleClickDelete, onReimport, variantId]);

  const columns: Array<MRT_ColumnDef<MachineAccountConfiguration>> = useMemo(
    () => [
      {
        header: "E-Mail",
        accessorKey: TableColumn.Email,
        Cell: ({ row }) => (
          <Anchor
            component="button"
            type="button"
            onClick={() => handleEmailClick(row.original)}
          >
            {row.original.email}
          </Anchor>
        )
      },
      { header: "Dateiformat", accessorKey: TableColumn.ParameterSet }
    ],
    []
  );

  const mrtTable = useMantineReactTable<MachineAccountConfiguration>({
    ...getDefaultMRTOptions({
      initialState: {
        sorting: [{ id: TableColumn.ParameterSet, desc: false }]
      },
      hideBorder: true
    }),
    ...getMRTTopToolbarCustomActionsRenderer({
      create: {
        text: "Neue Adresse einrichten",
        onClick: () => setActiveModal({ type: ModalType.New, item: null })
      },
      delete: { hide: true }
    }),
    ...getDefaultMRTRowEditModeOptions({
      rowActions: {
        other: shouldShowStaffView
          ? [
              {
                icon: IconRefresh,
                loading: (row) => reimportingIds.includes(row.id),
                text: "Machine Account files reimport",
                onClick: (row) => {
                  const inProgress =
                    reimportingIds.includes(row.original.id) ||
                    row.original.status ===
                      MachineAccountReimportStatus.InProgress;

                  return inProgress
                    ? undefined
                    : setActiveModal({
                        type: ModalType.Reimport,
                        item: row.original
                      });
                }
              }
            ]
          : [],
        edit: {
          text: "Machine Account bearbeiten",
          enableEdit: (cell) =>
            !reimportingIds.includes(cell.row.original.id) &&
            cell.row.original.status !==
              MachineAccountReimportStatus.InProgress,
          onClick: (row) => {
            mrtTable.setEditingRow(null);
            setActiveModal({ type: ModalType.Edit, item: row.original });
          }
        },
        delete: {
          text: "Machine Account löschen",
          enableDelete: (cell) =>
            !reimportingIds.includes(cell.row.original.id) &&
            cell.row.original.status !==
              MachineAccountReimportStatus.InProgress,
          onClick: (row) => {
            setActiveModal({
              type: ModalType.Delete,
              item: row.original
            });
          }
        }
      }
    }),
    enableRowSelection: false,
    columns: columns,
    data: machineAccountConfiguration || []
  });

  return (
    <LoadOrError
      className="MachineAccountPage"
      error={error}
      loading={isLoading}
    >
      <MantineReactTable table={mrtTable} />
      {renderModal()}
    </LoadOrError>
  );
}

export { MachineAccountPage };
